import { PERSONALIZATION_STORAGE_KEYS } from '../constants';
import type { IPersonalizationStorageAdapter } from '../types';

/**
 * Cookie configuration options for individual set/remove operations.
 */
export type CookieOptions = {
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  maxAge?: number;
};

/**
 * Minimal cookie store interface.
 * Injected via constructor to avoid browser/native cookie API dependency in packages/utils.
 *
 * Web implementation: wraps document.cookie
 * Native implementation: wraps @react-native-cookies/cookies CookieManager
 */
export type CookieStore = {
  get(name: string): string | null;
  set(name: string, value: string, options?: CookieOptions): void;
  remove(name: string, options?: CookieOptions): void;
};

/**
 * Adapter-level cookie storage options.
 * Passed to every set/remove call to ensure consistent cookie attributes across web and app.
 */
export type CookieStorageOptions = {
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  actorMaxAge?: number;
};

const DEFAULT_PATH = '/';
const DEFAULT_SECURE = true;
const DEFAULT_SAME_SITE = 'Lax' as const;
const DEFAULT_ACTOR_MAX_AGE = 365 * 24 * 60 * 60;

/**
 * Cookie-based storage adapter for app ↔ WebView session sync.
 *
 * The native app sets cookies on the WebView cookie store before navigation,
 * and this adapter reads/writes them via an injected CookieStore implementation.
 *
 * Storage strategy:
 * - `actorId`: persistent cookie (maxAge = actorMaxAge, default 1 year)
 * - `sessionId`: session cookie (no maxAge — app controls lifecycle)
 * - `sessionTimestamp`: session cookie (no maxAge — app controls lifecycle)
 *
 * @example
 * const adapter = new CookiePersonalizationStorageAdapter(cookieStore, {
 *   domain: '.skinseoul.com',
 * });
 * const client = new PersonalizationClient({ storage: adapter, ... });
 */
export class CookiePersonalizationStorageAdapter implements IPersonalizationStorageAdapter {
  private readonly store: CookieStore;
  private readonly baseOptions: CookieOptions;
  private readonly actorOptions: CookieOptions;

  constructor(store: CookieStore, options?: CookieStorageOptions) {
    this.store = store;
    this.baseOptions = {
      domain: options?.domain,
      path: options?.path ?? DEFAULT_PATH,
      secure: options?.secure ?? DEFAULT_SECURE,
      sameSite: options?.sameSite ?? DEFAULT_SAME_SITE,
    };
    this.actorOptions = {
      ...this.baseOptions,
      maxAge: options?.actorMaxAge ?? DEFAULT_ACTOR_MAX_AGE,
    };
  }

  getActorId(): string | null {
    return this.store.get(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID);
  }

  setActorId(actorId: string): void {
    this.store.set(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID, actorId, this.actorOptions);
  }

  removeActorId(): void {
    this.store.remove(PERSONALIZATION_STORAGE_KEYS.ACTOR_ID, this.baseOptions);
  }

  getSessionId(): string | null {
    return this.store.get(PERSONALIZATION_STORAGE_KEYS.SESSION_ID);
  }

  setSessionId(sessionId: string): void {
    this.store.set(PERSONALIZATION_STORAGE_KEYS.SESSION_ID, sessionId, this.baseOptions);
  }

  removeSessionId(): void {
    this.store.remove(PERSONALIZATION_STORAGE_KEYS.SESSION_ID, this.baseOptions);
  }

  getSessionTimestamp(): number | null {
    const raw = this.store.get(PERSONALIZATION_STORAGE_KEYS.SESSION_TS);
    if (raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  setSessionTimestamp(timestamp: number): void {
    this.store.set(PERSONALIZATION_STORAGE_KEYS.SESSION_TS, String(timestamp), this.baseOptions);
  }

  removeSessionTimestamp(): void {
    this.store.remove(PERSONALIZATION_STORAGE_KEYS.SESSION_TS, this.baseOptions);
  }
}
